import { Button } from 'components';
import { PartnerForm } from 'components/Forms/PartnerForm';
import { FormMessage } from 'components/Message';
import { DashboardTitle } from 'components/Titles';
import { useDisclose } from 'hooks/utilHooks';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { partnerValidationFromAdmin } from 'schemas';
import { PartnerService } from 'services';
import { IPartnerCreate, partnerDefaultValues } from 'types/Partner';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  useTheme,
} from '@mui/material';

export const PartnerCreateUpdate: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { goBack } = useHistory();
  const { palette } = useTheme();
  const { id } = useParams<{ id: string }>();

  const { isOpen, onClose, onOpen } = useDisclose();

  const form = useForm<IPartnerCreate>({
    defaultValues: partnerDefaultValues,
    resolver: partnerValidationFromAdmin,
    mode: 'onBlur',
  });

  const onSubmit = async (values: IPartnerCreate) => {
    try {
      setLoading(true);

      await PartnerService.createOrUpdatePartner({
        ...values,
        address: values.address?.cep ? values.address : undefined,
      });

      if (id) {
        setSuccess('Parceiro atualizado com sucesso.');
        onOpen();
      } else {
        form.reset(partnerDefaultValues);
        setSuccess('Parceiro cadastrado com sucesso.');
      }
    } catch (reason) {
      const { message } = reason as Error;
      setError(message);
    } finally {
      setLoading(false);

      setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
    }

    return;
  };

  const getPartner = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);

    try {
      const partner = await PartnerService.findById({ id });
      form.reset({
        ...partner,
        discountsUpdate: partner?.discounts,
      });
    } catch (reason) {
      const { message } = reason as Error;
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [form, id]);

  useEffect(() => {
    getPartner();
  }, [getPartner]);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <DashboardTitle title="GESTÃO DE PARCEIROS" />
        </Grid>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{ border: '2px solid', borderColor: palette.primary.main }}
          >
            {loading && <LinearProgress />}
            <FormProvider {...form}>
              <PartnerForm />
            </FormProvider>
          </Paper>

          {error && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={error} severity="error" />
                </Grid>
              </Grid>
            </Box>
          )}

          {success && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={success} severity="success" />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box py={1}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Button onClick={goBack}>voltar</Button>
              </Grid>
              <Grid item>
                <Button
                  disabled={loading}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {id ? 'ATUALIZAR' : 'CADASTRAR'}

                  {loading && (
                    <CircularProgress
                      size="small"
                      sx={{ width: 24, height: 24, ml: 2 }}
                    />
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle>
          Confirmação de envio
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText width={500}>
            Informações enviadas com sucesso!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

